import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import debounce from 'lodash.debounce'

import { dispatch } from 'store'
import { editorThunks } from 'store/features/editor/thunks'
import { WebWoodEngine } from '@renderforest/webwood-engine'
import { readEditorText } from '../dom-text'

const PERSIST_DEBOUNCE_MS = 300

// Strong RTL scripts: Hebrew, Arabic, Syriac, Thaana, NKo + the Arabic
// presentation-form blocks. Used as a tripwire — RTL hit-test would need
// the worker to emit logical/visual order and bidi metadata, which today
// it does not. When detected, we degrade gracefully (no word-expansion on
// double-click) rather than silently mis-select.
const RTL_SCRIPT = /[\u0590-\u08FF\uFB1D-\uFDFF\uFE70-\uFEFF]/

// Unicode-aware "word char" for the legacy fallback when `Intl.Segmenter`
// isn't available (very old browsers). Letters, digits, underscore.
const WORD_CHAR = /[\p{L}\p{N}_]/u

const hasSegmenter = (): boolean =>
  typeof Intl !== 'undefined' && 'Segmenter' in Intl

/**
 * Expand outward from `index` to native-grade word boundaries, matching
 * the platform's double-click-selects-word behavior. Uses `Intl.Segmenter`
 * when available so emoji clusters, hyphenated words, contractions, and
 * non-ASCII scripts behave correctly. Falls back to a simple `\p{L}\p{N}_`
 * sweep on browsers without Segmenter (covers our current iOS Safari
 * target — Segmenter shipped in iOS 14.5+).
 *
 * For strong-RTL text we return a collapsed range — the bitmap renderer
 * doesn't surface bidi metadata yet, so word-expansion would silently
 * mis-select. Tracked as a deferred RTL pass.
 */
const findWordBoundaries = (
  text: string,
  index: number
): { start: number; end: number } => {
  if (!text) return { start: 0, end: 0 }
  if (RTL_SCRIPT.test(text)) return { start: 0, end: 0 }
  const clamped = Math.max(0, Math.min(index, text.length))

  if (hasSegmenter()) {
    const Segmenter = (
      Intl as unknown as {
        Segmenter: new (
          locale: string | undefined,
          opts: { granularity: 'word' }
        ) => {
          segment: (input: string) => Iterable<{
            index: number
            segment: string
            isWordLike?: boolean
          }>
        }
      }
    ).Segmenter
    const segmenter = new Segmenter(undefined, { granularity: 'word' })
    for (const seg of segmenter.segment(text)) {
      if (!seg.isWordLike) continue
      const segEnd = seg.index + seg.segment.length
      if (clamped >= seg.index && clamped <= segEnd) {
        return { start: seg.index, end: segEnd }
      }
    }
    return { start: clamped, end: clamped }
  }

  // Fallback: bidirectional sweep over word-like chars. When the cursor sits
  // between two chars, prefer the one on the right (matches native
  // dblclick); at end-of-text, fall back to the char on the left.
  const probe = clamped < text.length ? clamped : Math.max(0, clamped - 1)
  if (!WORD_CHAR.test(text[probe])) return { start: clamped, end: clamped }
  let start = probe
  while (start > 0 && WORD_CHAR.test(text[start - 1])) start--
  let end = probe + 1
  while (end < text.length && WORD_CHAR.test(text[end])) end++
  return { start, end }
}

export const useEventsCallbacks = (
  canvasScale: number,
  updateObjectById: (id: string, data: Record<string, unknown>) => void,
  engineId: string,
  editorRef: React.RefObject<HTMLDivElement | null>,
  initialTextRef: React.MutableRefObject<string>,
  itemId: string,
  assetId: string,
  engine: WebWoodEngine | null,
  rotation: number,
  flipX: boolean
) => {
  const pushLiveText = useCallback(
    (text: string) => {
      updateObjectById(engineId, {
        metadata: { text }
      })
    },
    [updateObjectById, engineId]
  )

  // Debounced Redux persist while the user is typing. Mirrors the sidebar
  // TextInput pattern — Redux gets periodic updates (good for crash
  // recovery + intermediate undo entries) without firing on every keystroke.
  const debouncedPersist = useMemo(
    () =>
      debounce((text: string) => {
        dispatch(editorThunks.persistEditingText({ id: itemId, assetId, text }))
      }, PERSIST_DEBOUNCE_MS),
    [itemId, assetId]
  )

  // Cancel the pending persist on unmount and whenever the debounced fn
  // changes (id/assetId swap) so a stale callback doesn't fire after the
  // editor closes.
  useEffect(() => {
    return () => {
      debouncedPersist.cancel()
    }
  }, [debouncedPersist])

  // IME / dead-key composition gating. While the user is mid-composition
  // (CJK input, dead-key accents), `input` events fire on every keystroke
  // with the partial composition string. Rendering those partials through
  // the engine produces flicker AND would persist intermediate garbage if
  // the user cancels the composition before committing. We freeze the
  // pipeline until `compositionend`, then flush the finalized text.
  const isComposingRef = useRef(false)

  const handleCompositionStart = useCallback(() => {
    isComposingRef.current = true
  }, [])

  const handleCompositionEnd = useCallback(() => {
    isComposingRef.current = false
    // Some browsers fire `input` BEFORE `compositionend`; others fire it
    // after, others not at all. Force a flush of the finalized text here
    // so the engine doesn't miss the commit.
    const el = editorRef.current
    if (!el) return
    const text = readEditorText(el)
    pushLiveText(text)
    debouncedPersist(text)
  }, [editorRef, pushLiveText, debouncedPersist])

  const handleInput = useCallback(() => {
    if (isComposingRef.current) return
    const el = editorRef.current
    if (!el) return
    const text = readEditorText(el)
    pushLiveText(text)
    debouncedPersist(text)
  }, [pushLiveText, debouncedPersist, editorRef])

  const commit = useCallback(() => {
    const el = editorRef.current
    const text = el ? readEditorText(el) : initialTextRef.current
    // The pending debounce would fire after the edit-state was already
    // cleared by commitTextEdit (which gates on editingTextId === id) —
    // cancel it so we don't waste a Redux dispatch.
    debouncedPersist.cancel()
    dispatch(editorThunks.commitTextEdit({ id: itemId, text }))
  }, [itemId, editorRef, initialTextRef, debouncedPersist])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        // Escape exits edit mode but KEEPS the typed text (commit, not
        // cancel) — matches the user's expectation that typed content
        // should never be lost without an explicit undo action.
        e.preventDefault()
        commit()
        return
      }
      if (e.key === 'Enter') {
        // Force a plain `\n` rather than contentEditable's default `<div>` /
        // `<p>` block split. `insertLineBreak` inserts a single <br>, which
        // `innerText` reads back as `\n` — round-trip-safe.
        e.preventDefault()
        document.execCommand('insertLineBreak')
        const el = editorRef.current
        if (el) pushLiveText(readEditorText(el))
      }
    },
    [commit, pushLiveText, editorRef]
  )

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault()
    const text = e.clipboardData.getData('text/plain')
    document.execCommand('insertText', false, text)
  }, [])

  const stopPointer = useCallback(
    (e: React.PointerEvent | React.MouseEvent) => {
      e.stopPropagation()
      // iOS WebKit re-runs its own contentEditable cursor-positioning on the
      // synthesized mousedown/click that follow a touch — using DOM glyph
      // metrics that don't match the engine bitmap, so the cursor snaps to
      // the wrong char (often start/end). preventDefault here suppresses
      // that second pass; our pointerdown handler has already placed the
      // cursor via the engine's hit-test.
      e.preventDefault()
    },
    []
  )

  /**
   * Resolve a source-text index to a DOM (node, offset) anchor inside the
   * editor's tree. Treats `<br>` as a single `\n` character — mirrors the
   * inverse mapping done by `indexFromDomPosition`.
   */
  const resolveDomPosition = useCallback(
    (target: number): { node: Node; offset: number } | null => {
      const root = editorRef.current
      if (!root) return null
      let remaining = target
      let found: { node: Node; offset: number } | null = null

      const visit = (node: Node): boolean => {
        if (found) return true
        if (node.nodeType === Node.TEXT_NODE) {
          const len = (node as Text).length
          if (remaining <= len) {
            found = { node, offset: remaining }
            return true
          }
          remaining -= len
          return false
        }
        if (node.nodeName === 'BR') {
          if (remaining === 0) {
            const parent = node.parentNode
            if (parent) {
              const idx = Array.prototype.indexOf.call(parent.childNodes, node)
              found = { node: parent, offset: idx }
            }
            return true
          }
          remaining -= 1
          return false
        }
        for (let i = 0; i < node.childNodes.length; i++) {
          if (visit(node.childNodes[i])) return true
        }
        return false
      }
      visit(root)

      if (!found) {
        // Past the end — anchor at the root's last position.
        found = { node: root, offset: root.childNodes.length }
      }
      return found
    },
    [editorRef]
  )

  const setSelectionAtIndex = useCallback(
    (target: number) => {
      const pos = resolveDomPosition(target)
      if (!pos) return
      const range = document.createRange()
      range.setStart(pos.node, pos.offset)
      range.collapse(true)
      const sel = window.getSelection()
      sel?.removeAllRanges()
      sel?.addRange(range)
    },
    [resolveDomPosition]
  )

  /**
   * Set a (non-collapsed) DOM Selection where `anchor` is the fixed end and
   * `focus` is the moving end. Used during drag-to-select so the selection
   * grows / shrinks from the original mousedown index.
   */
  const setSelectionRange = useCallback(
    (anchor: number, focus: number) => {
      const a = resolveDomPosition(anchor)
      const f = resolveDomPosition(focus)
      if (!a || !f) return
      const sel = window.getSelection()
      if (!sel) return
      sel.removeAllRanges()
      // setBaseAndExtent preserves the anchor/focus distinction (which Range
      // alone doesn't) so subsequent updateCursor reads them correctly.
      sel.setBaseAndExtent(a.node, a.offset, f.node, f.offset)
    },
    [resolveDomPosition]
  )

  /**
   * Map a viewport (clientX, clientY) to a source-text char index via the
   * engine's bitmap hit-test. Used by single-click cursor placement,
   * drag-to-select, and double-click word selection — keep it in hook scope
   * so each call site reads a fresh `getBoundingClientRect()` (the canvas
   * may have been scrolled/zoomed between events).
   *
   * Handles rotation + horizontal flip: `getBoundingClientRect()` returns the
   * AABB of the rotated element, not its true top-left, so the click point
   * must be un-rotated around the AABB center before mapping to bitmap-local
   * coords. The CSS chain on `Styled.ItemFrame` is `rotate(...) scaleX(...)`,
   * so the flip is undone in the post-rotation local frame.
   */
  const hitTest = useCallback(
    (clientX: number, clientY: number): number | null => {
      const root = editorRef.current
      if (!engine || !root) return null
      const scale = canvasScale > 0 ? canvasScale : 1
      const rect = root.getBoundingClientRect()

      let localX: number
      let localY: number
      if (rotation === 0) {
        // Fast path — for axis-aligned elements rect.left/top is the real top-left.
        localX = (clientX - rect.left) / scale
        localY = (clientY - rect.top) / scale
      } else {
        // The AABB center coincides with the rotated element's center.
        const cx = rect.left + rect.width / 2
        const cy = rect.top + rect.height / 2
        const dx = clientX - cx
        const dy = clientY - cy
        const r = (-rotation * Math.PI) / 180
        const cos = Math.cos(r)
        const sin = Math.sin(r)
        const ux = dx * cos - dy * sin
        const uy = dx * sin + dy * cos
        const elemVpW = root.offsetWidth * scale
        const elemVpH = root.offsetHeight * scale
        localX = (ux + elemVpW / 2) / scale
        localY = (uy + elemVpH / 2) / scale
      }

      if (flipX) {
        localX = root.offsetWidth - localX
      }
      return (
        engine.getEditorText(engineId)?.hitTestCharIndex(localX, localY) ?? null
      )
    },
    [engine, engineId, canvasScale, rotation, flipX, editorRef]
  )

  const handleEditorPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return
      e.stopPropagation()
      // Block the browser's native click-to-position — its hit-test uses DOM
      // glyph metrics which don't match the engine-rendered glyphs the user
      // sees. We compute the index ourselves below and set the selection.
      e.preventDefault()
      const root = editorRef.current
      if (!root) return

      const anchorIdx = hitTest(e.clientX, e.clientY)
      if (anchorIdx === null) return

      root.focus()
      setSelectionAtIndex(anchorIdx)
      // iOS WebKit re-runs its native cursor placement on the synthesized
      // mouse events that follow a touch, even with preventDefault on the
      // pointerdown / mousedown / click chain — the cursor briefly lands at
      // our index then snaps to end-of-text and opens the native edit menu.
      // Re-applying on the next frame wins the race so our hit-test sticks.
      requestAnimationFrame(() => setSelectionAtIndex(anchorIdx))

      // Track pointermove for drag-to-select. The `anchor` stays at the
      // initial press; the `focus` follows the pointer until release.
      let lastFocusIdx = anchorIdx
      const onMove = (moveEvent: PointerEvent) => {
        const focusIdx = hitTest(moveEvent.clientX, moveEvent.clientY)
        if (focusIdx === null || focusIdx === lastFocusIdx) return
        lastFocusIdx = focusIdx
        if (focusIdx === anchorIdx) {
          setSelectionAtIndex(anchorIdx)
        } else {
          setSelectionRange(anchorIdx, focusIdx)
        }
      }
      const onUp = () => {
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
        window.removeEventListener('pointercancel', onUp)
      }
      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onUp)
      window.addEventListener('pointercancel', onUp)
    },
    [hitTest, setSelectionAtIndex, setSelectionRange, editorRef]
  )

  /**
   * Native inputs select the word under the pointer on double-click. We
   * can't use the browser's built-in behavior because its glyph hit-test
   * runs against the transparent DOM text, not the engine bitmap the user
   * actually sees — so it lands on the wrong word. Hit-test through the
   * engine, then expand to word boundaries against the live DOM text.
   */
  const handleDoubleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      const el = editorRef.current
      if (!el) return
      const charIndex = hitTest(e.clientX, e.clientY)
      if (charIndex === null) return
      const text = readEditorText(el)
      const { start, end } = findWordBoundaries(text, charIndex)
      if (end <= start) return
      el.focus()
      setSelectionRange(start, end)
    },
    [editorRef, hitTest, setSelectionRange]
  )

  return {
    handleInput,
    handleKeyDown,
    handlePaste,
    handleEditorPointerDown,
    handleDoubleClick,
    handleCompositionStart,
    handleCompositionEnd,
    stopPointer,
    commit,
    hitTest,
    setSelectionAtIndex
  }
}
